'use client';

import { flex, text, vstack } from '@onbeam/business-styled-system/patterns';
import { useFormContext } from '../../Form/FormContext';
import { FormSteps } from '../../Form/FormSteps';
import {
  ApplicationFormSchema,
  applicationFormSteps,
} from './ApplicationForm.utils';
import {
  Checkbox,
  DatePicker,
  Input,
  Radio,
  Select,
  Textarea,
  toast,
} from '@onbeam/business-ui';
import {
  ApplicationCategory,
  ApplicationProgressReportingFrequency,
  applicationProgressReportingFrequencyMap,
  ApplicationTeamType,
  applicationTeamTypeMap,
} from '../ApplicationsList/ApplicationsList.types';
import { ApplicationFormReview } from './ApplicationFormReview';
import { applicationStyleMap } from '../ApplicationsList/ApplicationsList.data';
import { Controller } from 'react-hook-form';
import { FormGroup } from '../../Form/FormGroup';
import { submitApplication } from '@/actions/submitApplication';
import { useRouter } from 'next/navigation';

export const ApplicationFormContent = () => {
  const router = useRouter();
  const {
    onContinue,
    onPrevious,
    onNavigate,
    activeIndex,
    isReviewing,
    form: {
      control,
      handleSubmit,
      register,
      formState: { errors },
    },
  } = useFormContext<ApplicationFormSchema>();

  const activeStep = applicationFormSteps[activeIndex];

  const onSubmit = async (values: ApplicationFormSchema) => {
    await submitApplication(values)
      .then(() => {
        toast.success('Thank you for your submission', {
          description: 'We’ll review it and get back to you',
        });
        router.push('/');
      })
      .catch((error) => {
        console.error('Error submitting application:', error);
        toast.error('There was an error submitting the form.', {
          description: 'Please verify your information and try again.',
        });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className={vstack({
          gap: '10',
          mt: '[3.75rem]',
          w: 'full',

          md: {
            mt: '10',
            maxW: '[26.25rem]',
            mx: 'auto',
          },
        })}
      >
        {isReviewing ? (
          <ApplicationFormReview />
        ) : (
          <>
            <div className={vstack({ gap: '4' })}>
              <h1
                className={text({
                  style: 'subtitle-md',
                })}
              >
                <span>{activeStep.title}</span>
                {activeStep.subtitle && (
                  <span className={text({ color: 'mono.tertiary' })}>
                    {' '}
                    {activeStep.subtitle}
                  </span>
                )}
              </h1>
              {activeStep.description && (
                <p
                  className={text({
                    style: 'body-md',
                    color: 'mono.tertiary',
                  })}
                >
                  {activeStep.description}
                </p>
              )}
            </div>
            <div className={vstack({ gap: '8', pb: '40' })}>
              {activeStep.fieldNames.map((fieldName) => {
                const defaultProps = {
                  errorMessage: errors[fieldName]?.message,
                  ...register(fieldName),
                };
                switch (fieldName) {
                  case 'name':
                    return (
                      <Input
                        key={fieldName}
                        label="Project overview"
                        placeholder="Enter your project name or title"
                        {...defaultProps}
                      />
                    );
                  case 'description':
                    return (
                      <Textarea
                        key={fieldName}
                        label="Describe your project"
                        placeholder="Tell us what you’re building, how it works, and why it matters for the Beam ecosystem."
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'applicant':
                    return (
                      <Input
                        key={fieldName}
                        label="Applicant name"
                        placeholder="Enter your name or team name"
                        {...defaultProps}
                      />
                    );
                  case 'teamType':
                    return (
                      <FormGroup
                        key={fieldName}
                        errorMessage={defaultProps.errorMessage}
                        label="Who’s working on this?"
                      >
                        <div className={flex({ wrap: 'wrap', gap: '2' })}>
                          {Object.values(ApplicationTeamType).map(
                            (teamType) => (
                              <Radio
                                {...register('teamType')}
                                variant="chip"
                                value={teamType}
                                key={teamType}
                              >
                                {applicationTeamTypeMap[teamType]}
                              </Radio>
                            ),
                          )}
                        </div>
                      </FormGroup>
                    );
                  case 'teamDescription':
                    return (
                      <Textarea
                        key={fieldName}
                        placeholder="List your team members, roles, and a bit about your experience."
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'email':
                    return (
                      <Input
                        key={fieldName}
                        type="email"
                        label="Contact email"
                        placeholder="Enter email address"
                        {...defaultProps}
                      />
                    );
                  case 'fundingRequest':
                    return (
                      <Input
                        key={fieldName}
                        type="number"
                        label="How much funding are you requesting? (USD)"
                        placeholder="Enter amount"
                        {...defaultProps}
                      />
                    );
                  case 'detailedBudget':
                    return (
                      <Textarea
                        key={fieldName}
                        label="Break down your budget"
                        placeholder="Show us where the funds will go. Be as detailed as you can. (This will be shared publicly.)"
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'dateRange':
                    return (
                      <Controller
                        key={fieldName}
                        control={control}
                        name="dateRange"
                        render={({
                          field: { onChange, onBlur: _, ...field },
                          fieldState: { error },
                        }) => (
                          <FormGroup errorMessage={error?.message}>
                            <DatePicker
                              mode="range"
                              label="When will you start & finish?"
                              placeholder="Expected start and completion dates."
                              onValueChange={onChange}
                              {...field}
                            />
                          </FormGroup>
                        )}
                      />
                    );
                  case 'milestones':
                    return (
                      <Textarea
                        key={fieldName}
                        label="Key milestones"
                        placeholder="List major goals or checkpoints along the way."
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'rationale':
                    return (
                      <Textarea
                        key={fieldName}
                        label="Why is your project a great fit for Beam?"
                        placeholder="Tell us what unique value you bring to the ecosystem."
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'categories':
                    return (
                      <FormGroup
                        key={fieldName}
                        errorMessage={defaultProps.errorMessage}
                        label="How does your project connect with Beam?"
                      >
                        <div className={flex({ wrap: 'wrap', gap: '2' })}>
                          {Object.values(ApplicationCategory).map(
                            (category) => (
                              <Checkbox
                                {...register('categories')}
                                variant="chip"
                                value={category}
                                key={category}
                              >
                                {applicationStyleMap[category].name}
                              </Checkbox>
                            ),
                          )}
                        </div>
                      </FormGroup>
                    );
                  case 'progressReportingFrequency':
                    return (
                      <Controller
                        key={fieldName}
                        control={control}
                        name="progressReportingFrequency"
                        render={({ field, fieldState: { error } }) => (
                          <FormGroup errorMessage={error?.message}>
                            <Select
                              label="How often will you report progress?"
                              placeholder="Choose a reporting frequency"
                              items={Object.values(
                                ApplicationProgressReportingFrequency,
                              ).map((frequency) => ({
                                name: applicationProgressReportingFrequencyMap[
                                  frequency
                                ],
                                value: frequency,
                              }))}
                              {...field}
                            />
                          </FormGroup>
                        )}
                      />
                    );
                  case 'demoURL':
                    return (
                      <Input
                        key={fieldName}
                        type="url"
                        label="Link to demo, MVP, or repo"
                        placeholder="Have something to show? Paste the link here."
                        {...defaultProps}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </>
        )}
      </div>
      {!isReviewing && (
        <FormSteps
          steps={applicationFormSteps}
          activeStep={activeIndex}
          onContinue={onContinue}
          onPrevious={onPrevious}
          onNavigate={onNavigate}
        />
      )}
    </form>
  );
};
